import { AuthenticationResult, EndSessionRequest, PopupRequest, PublicClientApplication, RedirectRequest, SilentRequest } from '@azure/msal-browser';
import { VueConstructor } from 'vue';
import { loginRequest } from '../config/authConfig';

export interface MsalPlugin {
    instance: PublicClientApplication;
    loginPopup(request?: PopupRequest): Promise<AuthenticationResult>;
    loginRedirect(request?: RedirectRequest): Promise<void>;
    logout(request?: EndSessionRequest): Promise<void>;
    logoutRedirect(request?: EndSessionRequest): Promise<void>;
    acquireTokenSilent(request: SilentRequest): Promise<AuthenticationResult>;
    acquireTokenPopup(request: PopupRequest): Promise<AuthenticationResult>;
    acquireTokenRedirect(request: RedirectRequest): Promise<void>;
    getAllAccounts(): any[];
    getActiveAccount(): any;
    setActiveAccount(account: any): void;
    handleRedirectPromise(): Promise<AuthenticationResult | null>;
    isAuthenticated(): boolean;
}

export class MSALPlugin implements MsalPlugin {
    instance: PublicClientApplication;

    constructor(msalInstance: PublicClientApplication) {
        this.instance = msalInstance;
    }

    async loginPopup(request?: PopupRequest): Promise<AuthenticationResult> {
        const popupRequest = request || {
            scopes: ['openid', 'profile']
        };
        return await this.instance.loginPopup(popupRequest);
    }

    async loginRedirect(request?: RedirectRequest): Promise<void> {
        const redirectRequest = request || {
            scopes: ['openid', 'profile']
        };
        return await this.instance.loginRedirect(redirectRequest);
    }

    async logout(request?: EndSessionRequest): Promise<void> {
        return this.logoutRedirect(request);
    }

    async logoutRedirect(request?: EndSessionRequest): Promise<void> {
        const logoutRequest = request || {
            account: this.instance.getActiveAccount()
        };
        return await this.instance.logoutRedirect(logoutRequest);
    }

    async acquireTokenSilent(request: SilentRequest): Promise<AuthenticationResult> {
        return await this.instance.acquireTokenSilent(request);
    }

    async acquireTokenPopup(request: PopupRequest): Promise<AuthenticationResult> {
        return await this.instance.acquireTokenPopup(request);
    }

    async acquireTokenRedirect(request: RedirectRequest): Promise<void> {
        return await this.instance.acquireTokenRedirect(request);
    }

    getAllAccounts(): any[] {
        return this.instance.getAllAccounts();
    }

    getActiveAccount(): any {
        return this.instance.getActiveAccount();
    }

    setActiveAccount(account: any): void {
        this.instance.setActiveAccount(account);
    }

    async handleRedirectPromise(): Promise<AuthenticationResult | null> {
        return await this.instance.handleRedirectPromise();
    }

    isAuthenticated(): boolean {
        const accounts = this.instance.getAllAccounts();
        return accounts.length > 0;
    }
}

const MSALVuePlugin = {
    install(Vue: VueConstructor, msalInstance: PublicClientApplication) {
        const msalPlugin = new MSALPlugin(msalInstance);

        Vue.prototype.$msal = msalPlugin;

        // For backward compatibility with vue-adal, create an $adal alias
        Vue.prototype.$adal = {
            login: () => msalPlugin.loginRedirect(loginRequest),
            logout: () => msalPlugin.logout(),
            isAuthenticated: () => msalPlugin.isAuthenticated(),
            getCachedToken: (resource: string): string | null => {
                // This would need to be implemented based on your specific needs
                // getCachedToken is deprecated. Use acquireTokenSilent instead.
                return null;
            }
        };
    }
};

export default MSALVuePlugin;

// TypeScript declarations
declare module 'vue/types/vue' {
    interface Vue {
        $msal: MsalPlugin;
        $adal: {
            login(): Promise<void>;
            logout(): Promise<void>;
            isAuthenticated(): boolean;
            getCachedToken(resource: string): string | null;
        };
    }
}
